import { flip, offset, shift, size, useFloating } from '@floating-ui/react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { goToSkyMapIdProfilePage } from '../../../services/authentication/skymap-id-profile-page';
import { TokenService } from '../../../services/authentication/token.service';
import {
  endZitadelSession,
  hasZitadelSession,
} from '../../../services/authentication/zitadel-user-manager';
import { LocalStorageHandler } from '../../../utils/core/local-storage-handler';
import { t } from '../../../utils/i18-next';
import { publish, SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { useOutsideClick } from '../../hooks/use-outside.clicked';
import { useAppContext } from '../../state/app-state';
import { useOptionalAuthState } from '../../state/auth-state';
import { useOptionalIntercomState } from '../../state/intercom-state';
import { useTheme } from '../../state/theme-state';
import { Stack } from '../stack/stack';

type OptionalNavigate = (to: string, options?: { replace?: boolean; state?: unknown }) => void;

const useOptionalNavigate = (): OptionalNavigate | undefined => {
  const navigationContext = React.useContext(
    NavigationContext as unknown as React.Context<{
      navigator: {
        push: (to: string, state?: unknown) => void;
        replace: (to: string, state?: unknown) => void;
      };
    } | null>,
  );

  return React.useMemo(() => {
    if (!navigationContext) {
      return undefined;
    }

    const { navigator } = navigationContext;

    return (to: string, options) => {
      if (options?.replace) {
        navigator.replace(to, options?.state);
      } else {
        navigator.push(to, options?.state);
      }
    };
  }, [navigationContext]);
};

const UserMenuDropdown = (
  { textColor, textHoverColor }: { textColor?: string; textHoverColor?: string } = {
    textColor: undefined,
    textHoverColor: undefined,
  },
) => {
  const themeContext = useTheme();
  const topMenuTextColor = textColor ?? themeContext.getTopMenuTextColor();
  const topMenuTextHoverColor = textHoverColor ?? themeContext.getTopMenuTextHoverColor();
  const isMoba = themeContext.isMobaTheme();
  const { currentUser } = useAppContext();
  const optionalAuthState = useOptionalAuthState();
  const authUser = optionalAuthState?.user;

  const [isDropdownOpen, setDropdownOpen] = useState(false);

  const initials = useMemo(() => {
    const firstName = currentUser?.firstName ?? authUser?.firstName ?? '';
    const lastName = currentUser?.lastName ?? authUser?.lastName ?? '';
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    const name = `${firstName} ${lastName}`.trim();
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return parts[0]?.[0]?.toUpperCase() ?? '';
  }, [currentUser, authUser]);

  const email = currentUser?.email ?? authUser?.email ?? '';

  const displayName = useMemo(() => {
    const currentUserName = currentUser
      ? `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim()
      : '';
    if (currentUserName.length > 0) {
      return currentUserName;
    }

    if (currentUser?.email) {
      return currentUser.email;
    }

    if (authUser) {
      const authName = `${authUser.firstName ?? ''} ${authUser.lastName ?? ''}`.trim();
      if (authName.length > 0) {
        return authName;
      }
      if (authUser.email) {
        return authUser.email;
      }
    }

    return '';
  }, [currentUser, authUser]);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-end',
    middleware: [
      offset({ mainAxis: 4, crossAxis: -8 }),
      flip(),
      shift(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            minWidth: `${rects.reference.width}px`,
          });
        },
      }),
    ],
    open: isDropdownOpen,
    onOpenChange: (nextOpen) => {
      setDropdownOpen(nextOpen);
    },
  });

  const containerRef = useRef<HTMLDivElement | null>(null);

  const mergedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node;
      refs.setReference(node);
    },
    [refs],
  );

  useOutsideClick(containerRef, (clickedOutside: boolean) => {
    if (isDropdownOpen && clickedOutside) {
      setDropdownOpen(false);
    }
  });

  const navigate = useOptionalNavigate();

  const { i18n } = useTranslation();

  const goToProfilePage = useCallback(async () => {
    setDropdownOpen(false);
    // A SkyMap Id person's name and sign-in methods live on SkyMap Id, not on our profile page.
    if (await hasZitadelSession()) {
      goToSkyMapIdProfilePage(i18n.language);
      return;
    }

    if (navigate) {
      navigate('/profile');
    } else {
      window.location.assign('/profile');
    }
  }, [navigate, i18n]);

  const optionalIntercomState = useOptionalIntercomState();

  const handleSignOut = useCallback(async () => {
    // Asked before anything is cleared: a Zitadel sign-out ends in a redirect, and what we do with
    // the screen in the meantime depends on knowing that up front.
    const isZitadelSession = await hasZitadelSession();

    publish(SubscriptionTopic.SignOut);
    setDropdownOpen(false);
    optionalIntercomState?.shutdown();
    LocalStorageHandler.instance.clearAll();

    if (isZitadelSession) {
      // Deliberately not clearing the auth state here. Doing so re-renders the app onto the
      // sign-in page for the moment before the browser leaves — so you would watch it appear,
      // jump to Zitadel, and appear again. Clearing storage is enough: the return trip boots a
      // fresh app, which finds no token and lands on the sign-in page once.
      new TokenService().remove();

      if (await endZitadelSession()) {
        return;
      }

      // Only reached when Zitadel could not be reached at all, so the user is still owed the
      // local sign-out below.
    }

    if (optionalAuthState?.logout) {
      await optionalAuthState.logout();
    } else {
      new TokenService().remove();
    }

    if (navigate) {
      navigate('/signin', { replace: true });
    } else {
      window.location.assign('/signin');
    }
  }, [optionalAuthState, optionalIntercomState, navigate]);

  return (
    <Component
      className={`${isDropdownOpen ? 'active' : ''} notranslate`}
      direction="column"
      ref={mergedRef}
      textColor={topMenuTextColor}
      textHoverColor={topMenuTextHoverColor}
      onClick={() => setDropdownOpen((prev) => !prev)}
    >
      <Header
        alignItems="center"
        aria-expanded={isDropdownOpen}
        aria-haspopup="menu"
        direction="row"
        role="button"
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setDropdownOpen((prev) => !prev);
          }
        }}
      >
        <InitialsAvatar $bgColor={topMenuTextColor} $isMoba={isMoba} className="notranslate">
          {initials}
        </InitialsAvatar>
      </Header>

      {isDropdownOpen && (
        <DropdownContainer
          ref={refs.setFloating}
          role="menu"
          style={floatingStyles}
          onClick={(e) => e.stopPropagation()}
        >
          <MenuContainer direction="column">
            <MenuItem
              $compact={true}
              clickable={true}
              onClick={() => {
                void goToProfilePage();
              }}
            >
              <Stack alignItems="center" direction="row" spacing="10px">
                <Stack direction="column" spacing="2px">
                  <ProfileName className="notranslate">{displayName}</ProfileName>
                  {email && <ProfileEmail className="notranslate">{email}</ProfileEmail>}
                </Stack>
              </Stack>
            </MenuItem>
            <MenuDivider />
            <MenuItem
              clickable={true}
              onClick={() => {
                void handleSignOut();
              }}
            >
              <span>{t('signOut', { ns: 'common' })}</span>
            </MenuItem>
          </MenuContainer>
        </DropdownContainer>
      )}
    </Component>
  );
};

const Component = styled(Stack)<{ textColor?: string; textHoverColor?: string }>`
  color: ${(props) => props.textColor ?? 'inherit'};
  cursor: pointer;
  padding: 10px 15px;
  justify-content: center;

  &.active,
  &:hover {
    background-color: ${(props) => props.theme.color.gray.darkest};
    color: ${(props) => props.textHoverColor ?? props.textColor ?? 'inherit'};
  }
`;

const Header = styled(Stack)``;

const InitialsAvatar = styled.div<{ $bgColor?: string; $isMoba?: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${(props) => props.$bgColor ?? '#fff'};
  color: ${(props) => (props.$isMoba ? '#fff' : props.theme.color.gray.darkest)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
  font-weight: 700;
  letter-spacing: 0.05em;
  user-select: none;
  flex-shrink: 0;

  ${(props) =>
    props.$isMoba &&
    css`
      ${Component}:hover &,
      ${Component}.active & {
        background-color: #fff;
        color: #000;
      }
    `}
`;

const DropdownContainer = styled.div``;

const MenuContainer = styled(Stack)`
  padding: 5px;
  background-color: #fff;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);
  border: 1px solid #aaa;
`;

const ProfileName = styled.span`
  font-size: 0.8em;
  font-weight: 500;
  color: ${(props) => props.theme.color.gray.dark};
`;

const ProfileEmail = styled.span`
  font-size: 0.7em;
  color: ${(props) => props.theme.color.gray.medium};
`;

const MenuDivider = styled.hr`
  margin: 4px 10px;
  border: none;
  border-top: 1px solid ${(props) => props.theme.color.gray.light};
`;

const MenuItem = styled.div<{ clickable: boolean; $compact?: boolean }>`
  padding: ${(props) => (props.$compact ? '8px' : '10px')};
  color: ${(props) => props.theme.color.gray.dark};

  ${(props) =>
    props.clickable &&
    css`
      &:hover {
        background-color: ${props.theme.color.gray.lightest};
        box-shadow: inset 5px 0 0 0 ${props.theme.color.brand.dark};
      }
      cursor: pointer;
    `}
`;

export { UserMenuDropdown };
